<!-- TODO: check darkmode colors -->
<template>
  <div
    class="flex gap-2 w-full"
    :class="{ 'text-muted': isCancelled }"
  >
    <div
      v-tippy="shiftTypeName"
      class="w-2 rounded-full"
      :style="'background-color:' + shiftColour"
    ></div>

    <div
      class="flex flex-col w-full border"
      style="border-color: var(--border-color); border-radius: var(--rounded-md)"
    >
      <div class="flex w-full flex-wrap gap-2 justify-between py-2 px-3">
        <div
          class="flex items-center gap-1"
          :class="{
            'line-through': isCancelled,
            'text-warning': totalAllocatedHours != shiftData.productiveHours
          }"
        >
          <div>{{ dayjs(shiftData.start, 'HH:mm:ss').format('HH:mm') }}</div>
          <Icon
            type="arrow-right"
            class="text-sm"
            :class="{
              'text-secondary': !isCancelled,
              'text-warning': totalAllocatedHours != shiftData.productiveHours
            }"
          />
          <div>{{ dayjs(shiftData.end, 'HH:mm:ss').format('HH:mm') }}</div>
          <Icon
            v-if="totalAllocatedHours != shiftData.productiveHours"
            v-tippy="{
              content: shiftData.productiveHours - totalAllocatedHours + ' unallocated hours'
            }"
            type="triangle-exclamation"
            class="text-sm"
            :class="!isCancelled ? 'text-warning' : ''"
          />
        </div>

        <div
          class="flex gap-2"
          :class="!isCancelled ? 'text-secondary' : ''"
        >
          <div
            v-tippy="'Productive Hours'"
            class="flex items-center gap-1"
          >
            <Icon
              type="user-clock"
              class="text-xs"
            />
            <div>{{ shiftData.productiveHours }}</div>
          </div>
          <div
            v-tippy="'Target Calls (Total)'"
            class="flex items-center gap-1"
          >
            <Icon
              type="phone"
              class="text-xs"
            />
            <div>{{ shiftData.callTarget * shiftData.productiveHours }}</div>
          </div>
          <div
            v-tippy="'Target Appointments (Total)'"
            class="flex items-center gap-1"
          >
            <Icon
              type="calendar"
              class="text-xs"
            />
            <div>{{ shiftData.appointmentTarget * shiftData.productiveHours }}</div>
          </div>
        </div>
      </div>

      <div v-if="shiftData.jobCards.length">
        <div
          class="w-full border-t"
          style="border-color: var(--border-color)"
        ></div>

        <div class="py-2 px-3">
          <ul style="list-style: circle">
            <li
              v-for="jobCard in shiftData.jobCards"
              :key="jobCard.id"
              class="flex justify-between"
            >
              <div style="font-size: var(--text-sm)">
                {{ jobCard.type == 'dealership' ? jobCard.dealershipName : jobCard.text }}
              </div>

              <div
                class="whitespace-nowrap"
                :class="!isCancelled ? 'text-secondary' : ''"
                style="font-size: var(--text-sm)"
              >
                {{ jobCard.hours + ' hr' }}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import Icon from '@/components/icon/Icon.vue'

const props = defineProps({
  shiftData: {
    type: Object,
    default: null
  },
  shiftTypes: {
    type: Array,
    default: () => []
  }
})

/**
 * Computed property to get the shift type name
 * Handles both numeric IDs and string names from the API
 */
const shiftTypeName = computed(() => {
  // If shiftData.type is already a string, use it directly
  if (typeof props.shiftData.type === 'string') {
    return props.shiftData.type
  }

  // If it's a number (ID), find the matching shift type
  if (typeof props.shiftData.type === 'number') {
    const matchingType = props.shiftTypes.find(type => type.id === props.shiftData.type)
    return matchingType?.name || String(props.shiftData.type)
  }

  // Fallback to string conversion
  return String(props.shiftData.type || '')
})

const isCancelled = computed(() => {
  const typeName = shiftTypeName.value
  return typeName === 'Absent' || typeName === 'Sick'
})

const shiftColour = computed(() => {
  // Find the matching shift type and return its color
  // Handle both numeric IDs and string names
  let matchingType
  if (typeof props.shiftData.type === 'number') {
    matchingType = props.shiftTypes.find(type => type.id === props.shiftData.type)
  } else {
    matchingType = props.shiftTypes.find(type => type.name === props.shiftData.type)
  }
  return matchingType?.color || '#ecf0f1' // Default color if no match found
})

const totalAllocatedHours = ref(0)
props.shiftData.jobCards.forEach(jobCard => {
  totalAllocatedHours.value += jobCard.hours
})
</script>

<style></style>
